import React, { ChangeEvent, Component, ComponentState } from 'react';
import LanguageUtil from 'util/LanguageUtil';
import { dispatch, RootState } from 'redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import Filter = query.Filter;
import empty = query.empty;
import Order = query.Order;
import _ from 'lodash';
import QueryBuilder = query.QueryBuilder;
import inside = query.inside;
import and = query.and;
import CraPolicyListViewerComponent from './CraPolicyListViewerComponent';
import { CustomFilterFields } from '../../../domain/ui/FilterDomains';
import { changePageFilter, clearPage } from 'redux/actions/PageActions';
import ValueFactory from 'util/ValueFactory';
import FilterUtil from 'util/FilterUtil';
import CustomPaginator, { CustomPaginatorProps } from 'components/custom/CustomPaginator';
import ItemToolsMenu from 'components/ItemToolsMenu';
import { CraSegmentType, CraDateSegmentsSchema, CraNumberSegmentsSchema, CraStringSegmentsSchema, CraVariableSchema } from '../../../domain/cra/CraDomains';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { setSelectedItems } from 'redux/actions/ComponentActions';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CollectionNames from '../../../constant/CollectionNames';
import eq = query.eq;

class CraPolicyListComponent extends Component<Props, State> {
  public state: State = new State();

  private segmentSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  constructor(props: Props) {
    super(props);
    this.prepareFilterFields();
  }

  componentDidMount(): void {
    this.prepareFilterFields();
    this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  protected startTracker = async () => {
    const { filter } = this.state;
    const { variable } = this.props;
    const { rows, currentPage } = this.state;
    const shouldPaginate = variable.type === CraSegmentType.STRING || variable.type === CraSegmentType.NUMBER || variable.type === CraSegmentType.DATE;
    let segmentQuery = query.QueryBuilder.select()
      .from(this.mapSegmentType(variable.type))
      .where(and(filter, eq('variable', variable.name)));
    if (shouldPaginate) {
      segmentQuery = segmentQuery.skip((currentPage - 1) * rows).limit(rows);
    } else {
      segmentQuery = segmentQuery.limit(1_000);
    }
    this.segmentSubscription = subscribe.Formica.subscribe(this.mapSegmentType(variable.type), segmentQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.segmentSubscription]);
  };

  protected stopTracker = async () => {
    await this.segmentSubscription?.stop();
    this.computation?.stop();
  };

  protected tracker = async () => {
    const { filter, rows, currentPage } = this.state;
    const { variable } = this.props;

    if (this.segmentSubscription?.ready()) {
      this.setState({ loading: true });

      const shouldPaginate = variable.type === CraSegmentType.STRING || variable.type === CraSegmentType.NUMBER || variable.type === CraSegmentType.DATE;

      let segmentQuery = query.QueryBuilder.select()
        .from(this.mapSegmentType(variable.type))
        .where(and(filter, eq('variable', variable.name)));

      if (shouldPaginate) {
        segmentQuery = segmentQuery.skip((currentPage - 1) * rows).limit(rows);
      } else {
        segmentQuery = segmentQuery.limit(1_000);
      }

      const result = await this.repository.findMany(segmentQuery.build());

      let totalRecord = 0;
      if (shouldPaginate) {
        const countQuery = query.QueryBuilder.select()
          .from(this.mapSegmentType(variable.type))
          .where(and(filter, eq('variable', variable.name)))
          .build();
        const countResult = await this.repository.count(countQuery);
        totalRecord = countResult || 0;
      }

      this.setState({
        loading: false,
        items: result as CraStringSegmentsSchema[] | CraNumberSegmentsSchema[] | CraDateSegmentsSchema[],
        totalRecord: shouldPaginate ? totalRecord : result.length,
      });
    }
  };

  protected getListView(): React.ReactNode {
    const { variable, onChange } = this.props;
    const { items } = this.state;

    return (
      <CraPolicyListViewerComponent
        views={true}
        currentModal={false}
        type={variable.type as CraSegmentType}
        segments={items}
        onSortChange={this.onSortChange}
        onDelete={this.onDelete}
        onChange={onChange}
      />
    );
  }

  protected onSortChange = (sorts: Order[]) => {
    this.setState({ sorts }, this.resubscribeTracker);
  };

  protected removeAll(): Promise<void> {
    const {
      component: { selectedComponentIds },
    } = this.props;
    // TODO: Add removeAll in craService in the future.
    return selectedComponentIds.flatMap((i: { id: string }) => i.id).forEach((itemId: string) => this.onDelete(itemId));
  }

  protected onDelete = (id: string, resub?: boolean) => {
    const { onDelete } = this.props;
    onDelete &&
      onDelete(id).then(() => {
        const {
          component: { selectedComponentIds },
        } = this.props;
        const updatedSelectedIds = selectedComponentIds.filter((itemId: any) => itemId !== id);
        dispatch(setSelectedItems(updatedSelectedIds));
        if (resub != false) {
          this.resubscribeTracker();
        }
      });
  };

  protected onChangeFilter = (filter: Filter) => {
    const isSame = _.isEqual(filter, this.state.filter);
    if (isSame) return;
    this.setState({ filter }, this.resubscribeTracker);
  };

  protected exportQueryParam = () => {
    const {
      router: { location, navigate },
    } = this.props;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', this.state.currentPage.toString());
    searchParams.set('row', this.state.rows.toString());
    navigate(location.pathname + '?' + searchParams.toString());
  };

  protected prepareFilterFields = async () => {
    const { variable } = this.props;
    const fields: CustomFilterFields[] = await FilterUtil.getCraSegmentFields(variable.type);
    dispatch(
      changePageFilter({
        filter: ValueFactory.initializeFilter(),
        items: fields,
      }),
    );
  };

  protected onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows ?? this.state.rows, currentPage: event.page ?? this.state.currentPage }, () => {
      this.exportQueryParam();
      this.resubscribeTracker();
    });
  };

  private mapSegmentType = (type: CraSegmentType) => {
    switch (type) {
      case CraSegmentType.STRING:
        return CollectionNames.CRA_STRING_SEGMENTS;
      case CraSegmentType.NUMBER:
        return CollectionNames.CRA_NUMBER_SEGMENTS;
      case CraSegmentType.DATE:
        return CollectionNames.CRA_DATE_SEGMENTS;
      default:
        throw new Error(`Unsupported segment type: ${type}`);
    }
  };

  public render() {
    const { items, filter, rows, totalRecord, currentPage, sorts } = this.state;
    const {
      component: { selectedComponentIds },
      variable,
    } = this.props;

    const showPagination = variable.type === CraSegmentType.STRING || variable.type === CraSegmentType.NUMBER || variable.type === CraSegmentType.DATE;

    return (
      <div className='w-full h-full flex flex-col overflow-y-hidden'>
        <div className='flex justify-start bg-white items-center w-full shrink-0'>
          <ItemToolsMenu
            views={true}
            id='item-tools-cra'
            itemsIsSelected={selectedComponentIds.length > 0}
            removeAllFunc={() => this.removeAll()}
            importVisible={false}
            exportVisible={false}
            onChangeFilter={this.onChangeFilter}
            componentLabel={LanguageUtil.get({ id: 'common.label.segment', defaultMessage: 'Segment' })}
            filter={filter}
            sessionCheck
          />
        </div>
        <div className='flex h-full flex-col min-h-0 overflow-hidden'>
          <div className='flex-1 min-h-0 overflow-hidden'>{this.getListView()}</div>
          {showPagination && (
            <div className='shrink-0 w-full bg-white z-10'>
              <CustomPaginator
                currentPage={currentPage}
                currentRecords={items.length}
                rows={rows}
                fixed={true}
                totalRecords={totalRecord}
                rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
                onPageChange={this.onPageChange}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

interface Props extends ReduxProps, FormicaProps {
  variable: CraVariableSchema;
  onDelete?: (id: string) => Promise<void>;
  onChange?(segment: CraStringSegmentsSchema | CraNumberSegmentsSchema | CraDateSegmentsSchema, segmentType: CraSegmentType): void;
}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  pagePath: state.page.pagePath,
  currentCategory: state.page.currentCategory,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes,
  policy: state.policy.policy,
});
const mapDispatchToProps = { clearPage, setSelectedItems, changePageFilter };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  filter: Filter = empty();
  loading: boolean = false;
  rows: number = 10;
  totalRecord: number = 0;
  currentPage: number = 1;
  sorts: Order[] = [];
  items: CraStringSegmentsSchema[] | CraNumberSegmentsSchema[] | CraDateSegmentsSchema[] = [];
}

export default withFormica(connector(CraPolicyListComponent));
